import React, { useState, useCallback, useEffect } from "react";
import { api } from "../lib/api";
import OpportunityCard from "../components/OpportunityCard";
import { useInfiniteList } from "../lib/useInfiniteList";
import { Loader2, RefreshCw, Sparkles } from "lucide-react";
import { useAuth } from "../lib/AuthContext";

export default function Opportunities() {
  const [minConv, setMinConv] = useState(0);
  const [memoStatus, setMemoStatus] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const { isAdmin } = useAuth();

  const loadMemoStatus = useCallback(() => {
    api.opportunityMemoStatus().then(setMemoStatus).catch(() => setMemoStatus(null));
  }, []);

  useEffect(() => {
    loadMemoStatus();
  }, [loadMemoStatus]);

  useEffect(() => {
    if (!memoStatus?.active) return undefined;
    const timer = window.setInterval(loadMemoStatus, 5000);
    return () => window.clearInterval(timer);
  }, [loadMemoStatus, memoStatus?.active]);

  const startBackfill = async () => {
    if (!window.confirm("Queue missing Concept Memos and AI packshots for every Opportunity? This uses image-generation credits.")) return;
    setBackfilling(true);
    setBackfillResult(null);
    try {
      const result = await api.backfillOpportunityMemos();
      setBackfillResult(result);
      loadMemoStatus();
    } finally {
      setBackfilling(false);
    }
  };

  const fetchPage = useCallback((skip, limit) => {
    return api.opportunities({ min_conviction: minConv, skip, limit });
  }, [minConv]);

  const { items, total, totalReady, hasMore, loading, sentinelRef } = useInfiniteList(fetchPage, minConv, 12);

  return (
    <div className="space-y-5">
      <div className="border-b border-white/10 pb-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-mono text-bm-amber">OPPORTUNITY PIPELINE</div>
          <h1 className="font-heading text-4xl font-black tracking-tight mt-1" data-testid="page-title">
            What Meolaa should build.
          </h1>
          <p className="text-sm text-bm-muted mt-2 max-w-2xl">
            Validated product opportunities with multi-layer conviction scoring, India localization, and GTM recommendations.
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-3">
          {isAdmin && memoStatus && memoStatus.completed < memoStatus.total && (
            <button type="button" onClick={startBackfill} disabled={backfilling || memoStatus.active > 0}
              data-testid="backfill-opportunity-memos"
              className="inline-flex items-center gap-2 px-3 py-2 border border-bm-cyan/50 text-bm-cyan font-mono text-xs hover:bg-bm-cyan hover:text-black transition-colors disabled:opacity-50">
              {backfilling || memoStatus.active > 0 ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {memoStatus.active > 0 ? `${memoStatus.active} MEMO JOBS ACTIVE` : "BACKFILL CONCEPT MEMOS"}
            </button>
          )}
          {memoStatus && (
            <span className="label-mono text-bm-dim" data-testid="opportunity-memo-progress">
              MEMOS {memoStatus.completed}/{memoStatus.total}
            </span>
          )}
          <span className="label-mono text-bm-dim">MIN CONVICTION</span>
          <div className="flex gap-1">
            {[0, 70, 80, 90].map((v) => (
              <button key={v} onClick={() => setMinConv(v)} data-testid={`filter-conv-${v}`}
                className={`px-2 py-1 text-xs font-mono border ${minConv === v ? "bg-bm-amber text-black border-bm-amber" : "border-white/10 text-bm-muted hover:text-white"}`}>
                {v === 0 ? "ALL" : `≥${v}%`}
              </button>
            ))}
          </div>
          <div className="label-mono text-bm-green" data-testid="opps-count">{totalReady ? `${items.length} / ${total}` : "…"}</div>
        </div>
      </div>

      {backfillResult && (
        <div className="border border-bm-green/40 bg-bm-green/5 p-3 font-mono text-xs text-bm-muted flex items-center gap-2" data-testid="opportunity-backfill-result">
          <RefreshCw className="w-4 h-4 text-bm-green" />
          Queued {backfillResult.queued} Opportunity artifact job{backfillResult.queued === 1 ? "" : "s"}; {backfillResult.already_complete} already complete.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="opps-grid">
        {items.map((o, i) => <OpportunityCard key={o.id} opp={o} idx={i} />)}
      </div>

      {hasMore && (
        <div ref={sentinelRef} className="flex items-center justify-center py-8" data-testid="opps-sentinel">
          <Loader2 className="w-4 h-4 animate-spin text-bm-cyan" />
          <span className="ml-2 font-mono text-xs text-bm-cyan">{loading ? "VALIDATING NEXT BATCH..." : "SCROLL FOR MORE"}</span>
        </div>
      )}

      {!hasMore && items.length > 0 && (
        <div className="text-center py-8 font-mono text-xs text-bm-dim" data-testid="opps-end">
          — END OF PIPELINE · {total} OPPORTUNITIES VALIDATED —
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="text-center py-12 font-mono text-sm text-bm-muted">No opportunities at this conviction threshold.</div>
      )}
    </div>
  );
}
